import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { SpeechService, VoiceCommand } from '../media/speech.service';
import { NavigationService } from '../core/navigation.service';
import { WaypointService } from '../core/waypoint.service';
import { AccountService } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { RecognizedVoiceCommand } from '../../models/recognized-voice-command';
import { DisplayMode } from '../../models/display-mode';
import { VoiceCommandConfirmationDialogComponent } from '../../components/modals/voice-command-confirmation-dialog/voice-command-confirmation-dialog.component';
import { VoiceCommandErrorDialogComponent } from '../../components/modals/voice-command-error-dialog/voice-command-error-dialog.component';
import { VoiceCommandListenDialogComponent } from '../../components/modals/voice-command-listen-dialog/voice-command-listen-dialog.component';

@Injectable({ providedIn: 'root' })
export class VoiceCommandFlowHandler {
  private commandSub: Subscription | null = null;
  private listenSub: Subscription | null = null;
  private navSub: Subscription | null = null;
  private isNavigating = false;

  private readonly speechService = inject(SpeechService);
  private readonly navigationService = inject(NavigationService);
  private readonly waypointService = inject(WaypointService);
  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly stackedModalsService = inject(StackedModalsService);

  bind(): void {
    if (!this.navSub) {
      this.navSub = this.navigationService.ObservableChangeNavigation.subscribe(resp => {
        if (resp === 'start') this.isNavigating = true;
        else if (resp === 'exit' || resp === 'arrive') this.isNavigating = false;
      });
    }
    if (!this.commandSub) {
      this.commandSub = this.speechService.ObservableRecognizedVoiceCommand.subscribe(
        (command: RecognizedVoiceCommand | null) => {
          if (command != null) this.handleCommand(command);
        },
      );
    }
    if (!this.listenSub) {
      this.listenSub = this.speechService.ObservableOpenVoiceCommandListen.subscribe(resp => {
        if (resp) {
          this.stackedModalsService.openModal(VoiceCommandListenDialogComponent, { disableClose: true });
        }
      });
    }
  }

  unbind(): void {
    this.commandSub?.unsubscribe();
    this.commandSub = null;
    this.listenSub?.unsubscribe();
    this.listenSub = null;
    this.navSub?.unsubscribe();
    this.navSub = null;
  }

  private handleCommand(command: RecognizedVoiceCommand): void {
    switch (command.CommadType) {
      case VoiceCommand.SetBattery:
        this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
          { data: { commandType: command.CommadType, batteryValue: command.Value }, autoFocus: false });
        break;
      case VoiceCommand.SkipWP:
        if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.RANGE && this.waypointService.getWaypointsParams().length > 2) {
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "Function is not available." } });
          this.speechService.PlayErrorNotAvailable();
        }
        break;
      case VoiceCommand.SkipCS:
        if (this.isNavigating) {
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "This function is only available during navigation." } });
          this.speechService.PlayErrorAvailableDuringNavigation();
        }
        break;
      case VoiceCommand.StopNavigation:
        if (this.isNavigating) {
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "This function is only available during navigation." } });
          this.speechService.PlayErrorAvailableDuringNavigation();
        }
        break;
      case VoiceCommand.SetWeight:
        this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
          { data: { commandType: command.CommadType, weightValue: command.Value }, autoFocus: false });
        break;
      case VoiceCommand.NavigateToHome: {
        const homeAddress = this.settingsService.getHomeAddress();
        if (this.accountService.getIsAuthorized() && homeAddress?.Description) {
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType, homeAddress: homeAddress }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "Function is not available." } });
          this.speechService.PlayErrorNotAvailable();
        }
        break;
      }
      case VoiceCommand.NavigateToWork: {
        const workAddress = this.settingsService.getWorkAddress();
        if (this.accountService.getIsAuthorized() && workAddress?.Description) {
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType, workAddress: workAddress }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "Function is not available." } });
          this.speechService.PlayErrorNotAvailable();
        }
        break;
      }
      case VoiceCommand.ReadEta:
        if (this.isNavigating) {
          let eta;
          this.navigationService.ObservableNextTurn.pipe(first()).subscribe((resp) => {
            if (resp) {
              const date = new Date();
              let dateInMinute = date.getHours() * 60 + date.getMinutes();
              eta = dateInMinute + resp.TimeToDest;
              if (eta > 60 * 24) {
                eta -= 60 * 24;
              }
            }
          });
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType, Eta: eta }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "This function is only available during navigation." } });
          this.speechService.PlayErrorAvailableDuringNavigation();
        }
        break;
      case VoiceCommand.ReadEte:
        if (this.isNavigating) {
          let ete;
          this.navigationService.ObservableNextTurn.pipe(first()).subscribe((resp) => {
            if (resp) {
              ete = resp.TimeToDest;
            }
          });
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType, Ete: ete }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "This function is only available during navigation." } });
          this.speechService.PlayErrorAvailableDuringNavigation();
        }
        break;
      case VoiceCommand.ReadRemainingDist:
        if (this.isNavigating) {
          let remainingDist;
          this.navigationService.ObservableNextTurn.pipe(first()).subscribe((resp) => {
            if (resp) {
              remainingDist = resp.DistToDest;
            }
          });
          this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
            { data: { commandType: command.CommadType, RemainingDist: remainingDist }, autoFocus: false });
        } else {
          this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
            { data: { errorMsg: "This function is only available during navigation." } });
          this.speechService.PlayErrorAvailableDuringNavigation();
        }
        break;
    }
  }
}
